import { useState } from 'react';
import { api, type Invariants, type Invariant, type WorkerPing, type JobAge } from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import { useResource } from '../lib/use-resource';
import { whenLocal, ageOf, dayLocal } from '../lib/time';
import './health.css';

/**
 * What is being watched, and what it cost last time it was not.
 *
 * THE INCIDENT IS THE SCREEN. Every check here carries the thing that actually
 * happened — ORBIT CO walking away, Kat Law suspended ninety minutes after
 * asking where her escalation stood, 88 closure notices proposed to accounts
 * that had already left — and it is shown whether the check is red or green.
 *
 * That is the whole reason the screen lists every check rather than only the
 * failing ones. A monitoring page that is blank when things are well teaches
 * the operator nothing on the days things are well, and a check whose purpose
 * nobody remembers is a check that gets deleted the first time it is
 * inconvenient. These are the memory of this system's own mistakes.
 */
// Named HealthBoard, not Health: `Health` is already the api type for
// GET /api/health (the header's "is the system running" line), and this screen
// reads GET /api/invariants. Two different questions that both deserve the
// word, so the screen takes the longer name.
export function HealthBoard({ onConfirm }: { onConfirm: (r: ConfirmRequest) => void }) {
  const r = useResource<Invariants>(() => api.invariants(), { pollMs: 60_000 });
  const [error, setError] = useState<string | null>(null);

  if (r.loading && !r.data) return <main className="board"><p className="list-empty">Reading the checks…</p></main>;
  if (!r.data) return <main className="board"><p className="list-empty list-error">{r.error?.message}</p></main>;

  const d = r.data;
  const failing = d.checks.filter((c) => c.state === 'failing' || c.state === 'broken');
  const acked = d.checks.filter((c) => c.state === 'acknowledged');
  const clear = d.checks.filter((c) => c.state === 'clear');

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); }
    r.refetch();
  };
  const acknowledge = (c: Invariant, days: number) => onConfirm({
    title: `Acknowledge “${c.label}” for ${days} days?`,
    detail: `It turns grey, stops making the board red and stops posting to Slack until `
      + `${dayLocal(new Date(Date.now() + days * 86400e3).toISOString())}. It comes back at once if a `
      + `row appears that is not one of these ${c.count}${c.sample.length ? ` (${c.sample.join(', ')}` + (c.count > c.sample.length ? ', …)' : ')') : ''}. `
      + 'Recorded with your name.',
    confirmLabel: 'Acknowledge',
    askReason: true,
    reasonRequired: true,
    reasonLabel: 'Why is this known and owned?',
    reasonPlaceholder: 'e.g. Hero ticket open',
    onConfirm: (reason) => act(() => api.acknowledge(c.id, days, reason)),
  });
  const unacknowledge = (c: Invariant) => onConfirm({
    title: `Clear the acknowledgement of “${c.label}”?`,
    detail: 'It will show red again, and the next health run posts it to Slack.',
    confirmLabel: 'Clear acknowledgement',
    tone: 'danger',
    onConfirm: () => act(() => api.acknowledge(c.id, 0)),
  });

  return (
    <main className="board">
      <div className="health-head">
        <div>
          <div className="mono eyebrow">Health</div>
          <h1>What is being watched, and what it cost last time</h1>
          <p className="lede">
            {d.ran_at === null
              ? 'No health run has happened yet.'
              : failing.length === 0
                ? `Everything is clear${acked.length ? `, ${acked.length} acknowledged` : ''}. Last checked ${ageOf(d.ran_at)} ago.`
                : `${failing.length} of ${d.checks.length} checks ${failing.length === 1 ? 'is' : 'are'} failing.`}
          </p>
        </div>
        <div className={`health-state ${failing.length ? 'is-bad' : 'is-good'}`}>
          <span className="mono health-state-label">{failing.length ? 'Attention' : 'Clear'}</span>
          <span className="mono health-state-when">
            {d.ran_at ? whenLocal(d.ran_at) : 'never run'}
          </span>
        </div>
      </div>

      <div className="health-grid">
        <section className="panel">
          <div className="panel-head">
            <h2>Workers</h2>
            <span className="mono panel-note">pinged over service bindings, not over the internet</span>
          </div>
          <div className="worker-rows">
            {Object.entries(d.pings).map(([name, p]) => <WorkerRow key={name} name={name} ping={p} />)}
            {Object.keys(d.pings).length === 0 && <p className="list-empty">No ping recorded yet.</p>}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Jobs</h2>
            <span className="mono panel-note">each worker against its own tolerance</span>
          </div>
          <div className="worker-rows">
            {Object.entries(d.jobs).map(([name, j]) => <JobRow key={name} name={name} job={j} />)}
            {Object.keys(d.jobs).length === 0 && <p className="list-empty">No run recorded yet.</p>}
          </div>
        </section>
      </div>

      {error && <p className="list-error health-error">{error}</p>}

      {failing.length > 0 && (
        <section className="panel panel-alarm">
          <div className="panel-head">
            <h2>Failing</h2>
          </div>
          {/* Long enough to need its own line. It answers the question an
              operator asks on seeing red here — "why did nobody tell me?" */}
          <p className="panel-prose posting-note">{d.posting_note}</p>
          {failing.map((c) => (
            <Check key={c.id} c={c} open maxDays={d.max_ack_days} onAcknowledge={acknowledge} />
          ))}
        </section>
      )}

      {acked.length > 0 && (
        <section className="panel">
          <div className="panel-head">
            <h2>Acknowledged</h2>
            <span className="mono panel-note">known and owned · back to red on its date or on a new row</span>
          </div>
          {acked.map((c) => <Check key={c.id} c={c} open onUnacknowledge={unacknowledge} />)}
        </section>
      )}

      <section className="panel">
        <div className="panel-head">
          <h2>Clear</h2>
          <span className="mono panel-note">with their incidents</span>
        </div>
        {clear.map((c) => <Check key={c.id} c={c} />)}
      </section>
    </main>
  );
}

const ACK_DAYS = [7, 14, 30, 60, 90];

/**
 * One check.
 *
 * The incident is always rendered. A failing check opens with its sample rows
 * and the acknowledge control; an acknowledged one with its reason, who and
 * until when; a clear one is collapsed to its label and incident, which is
 * still the most useful sentence on the page.
 */
function Check({ c, open = false, maxDays = 90, onAcknowledge, onUnacknowledge }: {
  c: Invariant; open?: boolean; maxDays?: number;
  onAcknowledge?: (c: Invariant, days: number) => void;
  onUnacknowledge?: (c: Invariant) => void;
}) {
  const [days, setDays] = useState(30);
  return (
    <details className={`check state-${c.state}`} open={open}>
      <summary>
        <span className={`dot dot-${c.state}`} aria-hidden />
        <span className="check-label">{c.label}</span>
        <span className="mono check-count">
          {c.state === 'failing' ? `${c.count} row${c.count === 1 ? '' : 's'}`
            : c.state === 'broken' ? 'could not run'
              : c.state === 'acknowledged' ? `acknowledged · ${c.count} row${c.count === 1 ? '' : 's'}`
                : 'clear'}
          {c.aside ? <span className="check-aside"> · {c.aside.count} {c.aside.label}</span> : null}
        </span>
      </summary>
      <div className="check-body">
        {c.error && <p className="check-error mono">The check itself failed: {c.error}</p>}
        {c.acknowledged && (
          <p className="check-ack">
            <strong>{c.acknowledged.reason}</strong>
            <span className="mono"> · until {dayLocal(c.acknowledged.until)} · by {c.acknowledged.by}</span>
          </p>
        )}
        {c.realert && (
          <p className="check-realert mono">
            {c.realert.reason === 'lapsed' ? `Acknowledged until ${dayLocal(c.realert.until)} — that has passed.`
              : c.realert.reason === 'new_rows' ? `Acknowledged, but new since then: ${c.realert.rows?.join(', ')}.`
                : 'Acknowledged, but the check could not run, so its rows cannot be compared.'}
          </p>
        )}
        {c.sample.length > 0 && (
          <p className="mono check-sample">
            {c.sample.join(', ')}{c.count > c.sample.length ? ` … and ${c.count - c.sample.length} more` : ''}
          </p>
        )}
        {c.aside && (
          <p className="mono check-aside-rows">
            {c.aside.count} {c.aside.label}: {c.aside.rows.join(', ')}
          </p>
        )}
        <p className="check-why">{c.why}</p>
        {/* The incident, in the operator's own history. This is the line that
            makes a green check worth reading. */}
        <p className="check-incident">{c.incident}</p>
        <p className="mono check-id">
          {c.id}{c.since ? ` · ${c.state} since ${whenLocal(c.since)}` : ''}
        </p>
        {onAcknowledge && c.state === 'failing' && (
          <div className="check-actions">
            <label className="mono check-days">
              for{' '}
              <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                {ACK_DAYS.filter((n) => n <= maxDays).map((n) => <option key={n} value={n}>{n} days</option>)}
              </select>
            </label>
            <button type="button" className="btn-ghost" onClick={() => onAcknowledge(c, days)}>Acknowledge…</button>
          </div>
        )}
        {onUnacknowledge && (
          <div className="check-actions">
            <button type="button" className="btn-ghost" onClick={() => onUnacknowledge(c)}>Clear acknowledgement…</button>
          </div>
        )}
      </div>
    </details>
  );
}

function WorkerRow({ name, ping }: { name: string; ping: WorkerPing }) {
  return (
    <div className="worker-row">
      <span className={`dot dot-${ping.ok ? 'clear' : 'failing'}`} aria-hidden />
      <span className="worker-name">{name}</span>
      <span className={`mono worker-state state-${ping.state}`}>{ping.state.replace(/_/g, ' ')}</span>
      <span className="mono worker-detail">
        {ping.missing_bindings?.length
          ? `missing ${ping.missing_bindings.join(', ')}`
          : ping.detail ?? (ping.deploy_sha ? `at ${ping.deploy_sha}` : '')}
      </span>
    </div>
  );
}

function JobRow({ name, job }: { name: string; job: JobAge }) {
  // `running` and `ok` are both fine; `stalled` is a run that never wrote its
  // finish, which would otherwise read as "running" for ever.
  const good = job.state === 'ok' || job.state === 'running';
  return (
    <div className="worker-row">
      <span className={`dot dot-${good ? 'clear' : 'failing'}`} aria-hidden />
      <span className="worker-name">{name}</span>
      <span className={`mono worker-state state-${job.state}`}>{job.state}</span>
      <span className="mono worker-detail">
        {job.age_minutes != null
          ? `${job.age_minutes} min ago · tolerance ${job.max_age_minutes} min`
          : job.detail ?? ''}
      </span>
    </div>
  );
}
